import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

interface MarkdownRendererProps {
  content: string;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * 通用 Markdown 渲染组件
 * 支持 GFM（表格、任务列表、删除线等）、HTML 标签、图片等
 */
const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({content, className, style}) => {
  return (
    <>
      <div className={`md-rendered ${className || ''}`} style={style}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
          {content}
        </ReactMarkdown>
      </div>
      <style>{`
        .md-rendered {
          line-height: 1.8;
          font-size: 14px;
          color: #333;
          word-wrap: break-word;
        }
        .md-rendered > *:first-child {
          margin-top: 0 !important;
        }
        .md-rendered > *:last-child {
          margin-bottom: 0 !important;
        }
        .md-rendered h1 {
          font-size: 20px;
          font-weight: 600;
          margin: 20px 0 10px;
          padding-bottom: 0;
          border-bottom: none;
        }
        .md-rendered h2 {
          font-size: 17px;
          font-weight: 600;
          margin: 18px 0 8px;
          padding-bottom: 0;
          border-bottom: none;
        }
        .md-rendered h3 {
          font-size: 15px;
          font-weight: 600;
          margin: 14px 0 6px;
        }
        .md-rendered h4, .md-rendered h5, .md-rendered h6 {
          font-size: 14px;
          font-weight: 600;
          margin: 12px 0 6px;
        }
        .md-rendered p {
          margin: 6px 0;
        }
        /* 关键：覆盖全局 list-style: none 重置，确保列表标识正常显示 */
        .md-rendered ul,
        .md-rendered ul ul,
        .md-rendered ul ul ul {
          list-style: disc !important;
          padding-left: 24px !important;
          margin: 6px 0;
        }
        .md-rendered ul ul {
          list-style: circle !important;
        }
        .md-rendered ul ul ul {
          list-style: square !important;
        }
        .md-rendered ol,
        .md-rendered ol ol,
        .md-rendered ol ol ol {
          list-style: decimal !important;
          padding-left: 24px !important;
          margin: 6px 0;
        }
        .md-rendered li {
          margin-bottom: 4px;
          display: list-item !important;
        }
        .md-rendered li > p {
          margin: 2px 0;
        }
        /* 任务列表特殊处理：含 checkbox 的列表项不显示标识 */
        .md-rendered .task-list-item,
        .md-rendered li.task-list-item {
          list-style: none !important;
        }
        .md-rendered li input[type="checkbox"] {
          margin-right: 6px;
        }
        .md-rendered code {
          background: #f5f7fa;
          color: #476582;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 13px;
          font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
        }
        .md-rendered pre {
          background: #f5f7fa;
          padding: 14px 18px;
          border-radius: 8px;
          overflow-x: auto;
          margin: 10px 0;
          border: 1px solid #eef0f4;
        }
        .md-rendered pre code {
          background: none;
          padding: 0;
          font-size: 13px;
          color: #333;
        }
        .md-rendered blockquote {
          border-left: 4px solid #d0d7de;
          padding: 4px 16px;
          color: #656d76;
          margin: 10px 0;
          background: #f8f9fa;
          border-radius: 0 6px 6px 0;
        }
        .md-rendered blockquote p {
          margin: 4px 0;
        }
        .md-rendered img {
          max-width: 100%;
          border-radius: 8px;
          margin: 8px 0;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        }
        .md-rendered video {
          max-width: 100%;
          border-radius: 8px;
          margin: 8px 0;
          background: #000;
        }
        .md-rendered table {
          width: 100%;
          border-collapse: collapse;
          margin: 10px 0;
          font-size: 13px;
        }
        .md-rendered th, .md-rendered td {
          border: 1px solid #e8e8e8;
          padding: 8px 12px;
          text-align: left;
        }
        .md-rendered th {
          background: #f5f7fa;
          font-weight: 600;
        }
        .md-rendered tr:nth-child(even) {
          background: #fafbfc;
        }
        .md-rendered hr {
          border: none;
          border-top: 1px solid #e8e8e8;
          margin: 16px 0;
        }
        .md-rendered a {
          color: #1677ff;
          text-decoration: none;
        }
        .md-rendered a:hover {
          text-decoration: underline;
        }
        .md-rendered strong {
          font-weight: 600;
        }
        .md-rendered del {
          color: #999;
        }
      `}</style>
    </>
  );
};

export default MarkdownRenderer;
